import React, { ReactElement, useEffect, useState } from 'react';
import './InsightsActions.scss';
import { InsightsActionsResponse } from '../../api/types';
import CopyButton from '../button/CopyButton';
import { List, ListItem } from '@patternfly/react-core';
import Markdown from './Markdown';

export type props = {
	insights: InsightsActionsResponse;
	hasBackground: boolean;
	/** Wrap content in a collapsible card with "Read more" */
	collapsible?: boolean;
	/** Max lines to show before collapsing (default 4) */
	collapsedMaxItems?: number;
};

const InsightsActions = (props: props): ReactElement => {
	const { insights, hasBackground, collapsible = false, collapsedMaxItems = 4 } = props;
	const [copyText, setCopyText] = useState('');
	const [isExpanded, setIsExpanded] = useState(false);

	useEffect(() => {
		let finalText = '';
		insights?.insights?.forEach((insight, index) => {
			if (index === 0) {
				finalText = `Insights\n`;
			}

			finalText = `${finalText}${insight}\n`;
		});

		insights?.actions?.forEach((action, index) => {
			if (index === 0) {
				finalText = `${finalText}Actions\n`;
			}

			finalText = `${finalText}${action}\n`;
		});

		setCopyText(finalText);
	}, [insights]);

	const totalItems = (insights?.insights?.length ?? 0) + (insights?.actions?.length ?? 0);
	const shouldCollapse = collapsible && totalItems > collapsedMaxItems && !isExpanded;

	const insightItems = insights?.insights ?? [];
	const actionItems = insights?.actions ?? [];

	// When collapsed, show limited items
	let visibleInsights = insightItems;
	let visibleActions = actionItems;

	if (shouldCollapse) {
		if (insightItems.length >= collapsedMaxItems) {
			visibleInsights = insightItems.slice(0, collapsedMaxItems);
			visibleActions = [];
		} else {
			visibleInsights = insightItems;
			visibleActions = actionItems.slice(0, collapsedMaxItems - insightItems.length);
		}
	}

	const content = (
		<div className={`insights-actions-container ${hasBackground ? 'insights-background' : ''}`}>
			<div className={'insights'}>
				<div className={'header'}>
					<b>Insights</b>
					<CopyButton
						hasIcon={true}
						textToCopy={copyText}
					/>
				</div>
				<List>
					{visibleInsights.map((insight, idx) => {
						return (
							<ListItem
								key={idx}
								className={'insight-item'}
							>
								<Markdown>{insight}</Markdown>
							</ListItem>
						);
					})}
				</List>
			</div>
			{visibleActions.length > 0 && (
				<div className={'actions'}>
					<div className={'header'}>
						<b>Actions</b>
					</div>
					<List>
						{visibleActions.map((action, idx) => {
							return (
								<ListItem
									key={idx}
									className={'insight-item'}
								>
									<Markdown>{action}</Markdown>
								</ListItem>
							);
						})}
					</List>
				</div>
			)}
			{collapsible && totalItems > collapsedMaxItems && (
				<button
					className="insights-read-more-btn"
					onClick={() => setIsExpanded(!isExpanded)}
				>
					{isExpanded ? 'Show less' : 'Read more'}
				</button>
			)}
		</div>
	);

	if (collapsible) {
		return <div className="insights-card">{content}</div>;
	}

	return content;
};

export default InsightsActions;
